import { useTranslation } from 'react-i18next'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeftIcon, MegaphoneIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '@/components/LoadingSpinner'
import { useAnnouncements } from '@/hooks/useAnnouncements'
import { useDateFormatter } from '@/hooks/useDateFormatter'

export default function AnnouncementDetailPage() {
  const { t } = useTranslation('home')
  const { id } = useParams<{ id: string }>()
  const { formatDate } = useDateFormatter()
  const { data: announcements, isLoading } = useAnnouncements()

  if (isLoading) {
    return <LoadingSpinner />
  }

  const announcement = announcements?.find((a) => String(a.id) === id)

  if (!announcement) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-4 text-center">
        <p className="text-base-content/60">{t('announcement_not_found')}</p>
        <Link to="/" className="btn btn-ghost btn-sm mt-4">
          <ArrowLeftIcon className="w-4 h-4" />
          {t('actions.back', { ns: 'common' })}
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-0 pb-6">
      {/* Header */}
      <div className="bg-primary px-4 pt-12 pb-5 flex items-center gap-3">
        <Link to="/" className="text-primary-content/80 hover:text-primary-content" aria-label="Back">
          <ArrowLeftIcon className="w-5 h-5" />
        </Link>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <MegaphoneIcon className="w-5 h-5 text-primary-content flex-shrink-0" />
          <h1 className="text-primary-content font-bold text-base truncate">
            {t('announcements_title')}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pt-5 space-y-4">
        <h2 className="text-xl font-bold text-base-content leading-snug">
          {announcement.title}
        </h2>

        {/* Metadata */}
        <div className="flex flex-col gap-1 text-xs text-base-content/60">
          <span>
            {t('published')}: {formatDate(announcement.published_at)}
          </span>
          {announcement.expires_at && (
            <span>
              {t('expires')}: {formatDate(announcement.expires_at)}
            </span>
          )}
        </div>

        <div className="divider my-1" />

        {/* Full body */}
        <p className="text-sm text-base-content leading-relaxed whitespace-pre-wrap">
          {announcement.body}
        </p>
      </div>
    </div>
  )
}
